import * as actionTypes from "./actionTypes";
import { Actions } from "./types";
import produce, { Draft } from "immer";
import { Alert } from "records/Alert";

export interface AppState {
	authenticated: boolean;
	isReady: boolean;
	notifUnread: number;
	alerts: Alert[];
	authType?: "password" | "azure";
	clientId?: string;
	tenant?: string;
	username: string;
	roles: { [key: string]: string };
	managedRoles: { [key: string]: string };
	legacyCheckboxes: { [key: string]: number[] };
	listCheckboxes: string[];
	formFullWidth: boolean;
}

//--------------------------
// Default app state
//--------------------------

export const defaultAppState = (): Readonly<AppState> => {
	return {
		authenticated: false,
		isReady: false,
		roles: {},
		managedRoles: {},
		notifUnread: 0,
		alerts: [],
		username: "",
		legacyCheckboxes: {},
		listCheckboxes: [],
		formFullWidth: false,
	};
};

//--------------------------
// Reducer
//--------------------------

const appReducer = (state = defaultAppState(), action: Actions): AppState => {
	return produce(state, draft => {
		switch (action.type) {
			case actionTypes.APP_READY: {
				appReady(draft);
				break;
			}

			case actionTypes.AUTHENTICATED: {
				authenticated(draft);
				break;
			}

			case actionTypes.NOT_AUTHENTICATED: {
				draft.roles = {};
				draft.authenticated = false;
				draft.isReady = true;
				draft.username = "";
				return draft;
			}

			case actionTypes.LOGOUT: {
				draft.authenticated = false;
				draft.isReady = false;
				break;
			}

			case actionTypes.CREATE_ALERT: {
				let isNewAlert = true;
				draft.alerts.forEach(alert => {
					if (
						action.alert.description &&
						alert.description === action.alert.description
					) {
						isNewAlert = false;
					}
				});
				if (isNewAlert) createAlert(draft, action.alert);
				break;
			}

			case actionTypes.DELETE_ALERT: {
				deleteAlert(draft, action.id);
				break;
			}

			case actionTypes.UPDATE_UNREAD: {
				updateUnread(draft, action.unreadCount);
				break;
			}

			case actionTypes.SET_AUTH_TYPE: {
				draft.authType = action.authType;
				draft.tenant = action.tenant;
				if (action.clientId) draft.clientId = action.clientId;
				break;
			}

			case actionTypes.SET_ROLES: {
				draft.roles = action.roles;
				break;
			}

			case actionTypes.SET_MANAGED_ROLES: {
				draft.managedRoles = action.managedRoles;
				break;
			}

			case actionTypes.SET_USERNAME: {
				draft.username = action.username;
				break;
			}

			case actionTypes.SET_LEGACY_CHECKBOXES: {
				draft.legacyCheckboxes[action.legacyId] = action.checkboxes;
				break;
			}

			case actionTypes.SET_CHECKED_BOXES: {
				draft.listCheckboxes = action.checkboxes;
				break;
			}

			case actionTypes.ADD_CHECKED_BOX: {
				draft.listCheckboxes.push(action.id);
				break;
			}

			case actionTypes.REMOVE_CHECKED_BOX: {
				draft.listCheckboxes.splice(
					draft.listCheckboxes.indexOf(action.id),
					1
				);
				break;
			}

			case actionTypes.FORM_FULL_WIDTH: {
				draft.formFullWidth = action.formFullWidth;
				break;
			}

			default: {
				return draft;
			}
		}
	});
};

//--------------------------
// State actions
//--------------------------

export const authenticated = (state: Draft<AppState>) => {
	state.alerts = [];
	state.authenticated = true;
};

export const appReady = (state: Draft<AppState>) => (state.isReady = true);

export const createAlert = (state: Draft<AppState>, alert: Alert) =>
	state.alerts.push(alert);

export const deleteAlert = (state: Draft<AppState>, id: string) => {
	state.alerts.forEach((item, index) => {
		if (item.id === id) {
			state.alerts.splice(index, 1);
		}
	});
};

export const updateUnread = (state: Draft<AppState>, unreadCount: number) =>
	(state.notifUnread = unreadCount);

export const setRoles = (
	state: Draft<AppState>,
	roles: { [key: string]: string }
) => {
	state.roles = roles;
};

// export reducer
export default appReducer;
